/* =====================================================
   🌐 Global Swiper Manager for all Shopify Sections
   -----------------------------------------------------
   ✅ Handles: video-product-showcase, shop-the-look,
               banner_with_featured_product,
               featured-collection-custom,
               category-section,
               wear-with-it,
               shop-the-look-2-0
   ✅ Lazy initialization (requestIdleCallback + IntersectionObserver)
   ✅ Prevents render blocking (LCP, INP, CLS, TBT)
   ✅ Shopify Online Store 2.0 safe
   ✅ Includes mobile vertical swiper direction support
   ✅ Includes vertical swiper blank fix (autoHeight & image load update)
   ===================================================== */
const IS_ANDROID = /Android/i.test(navigator.userAgent);

class GlobalSwiperManager {
  constructor() {
    this.instances = new Map();

    /* =================================================
       🔥 CHANGE #1 (ADDED – NOT REMOVED)
       -------------------------------------------------
       Above-the-fold / HERO swipers ko turant init karna
       (jinke paas data-swiper-priority="high" ho)
       ================================================= */
    this.initializeAboveTheFold();

    /* =================================================
       🔥 CHANGE #2 (ADDED – NOT REMOVED)
       -------------------------------------------------
       Baaki sab swipers ko deferred rakha
       (requestIdleCallback / timeout)
       ================================================= */
    this.initializeDeferred();

    /* =================================================
       Shopify dynamic section support (AS-IS)
       ================================================= */
    document.addEventListener("shopify:section:load", () => {
      this.initializeAboveTheFold();
      this.initializeDeferred();
    });

    document.addEventListener("shopify:section:unload", () => {
      this.destroyAll();
    });
  }

  /* =================================================
     🔥 ABOVE THE FOLD INIT (NEW – SAFE ADDITION)
     ================================================= */
  initializeAboveTheFold() {
    document
      .querySelectorAll(
        '[data-swiper-type][data-swiper-priority="high"]:not(.swiper-initialized)'
      )
      .forEach((el) => this.initSwiper(el));
  }

  /* =================================================
     ❗ ORIGINAL init() FUNCTION (PRESERVED)
     -------------------------------------------------
     ⚠️ NOTE:
     Is function ko remove NAHI kiya gaya
     Lekin constructor se call bhi NAHI ho raha
     Taaki double idle delay na aaye
     ================================================= */
  init() {
    if ("requestIdleCallback" in window) {
      requestIdleCallback(() => this.initializeAll(), { timeout: 2000 });
    } else {
      setTimeout(() => this.initializeAll(), 1000);
    }

    document.addEventListener("shopify:section:load", () => {
      this.initializeAll();
    });

    document.addEventListener("shopify:section:unload", () => {
      this.destroyAll();
    });
  }

  /* =================================================
     💤 DEFERRED INIT (MODIFIED – SAFE)
     -------------------------------------------------
     CHANGE:
     priority="high" wale swipers ko yahan SKIP kiya
     ================================================= */
  initializeDeferred() {
    const run = () => {
      document
        .querySelectorAll(
          '[data-swiper-type]:not([data-swiper-priority="high"]):not(.swiper-initialized)'
        )
        .forEach((el) => this.initSwiper(el));
    };

    if (IS_ANDROID) {
      setTimeout(run, 300); // ⚡ Android fast init
      return;
    }

    if ("requestIdleCallback" in window) {
      requestIdleCallback(run, { timeout: 2000 });
    } else {
      setTimeout(run, 800);
    }
  }

  /* =================================================
     🔁 ORIGINAL initializeAll (AS-IS)
     ================================================= */
  initializeAll() {
    document
      .querySelectorAll("[data-swiper-type]:not(.swiper-initialized)")
      .forEach((el) => this.initSwiper(el));
  }

  /* =================================================
     🧹 destroyAll (DUPLICATE FIX)
     -------------------------------------------------
     NOTE:
     Tumhare code me ye function 2 baar tha
     Ab sirf ek hi rakha gaya hai
     ================================================= */
  destroyAll() {
    this.instances.forEach((swiper) => {
      if (swiper && swiper.destroy) {
        swiper.destroy(true, true);
      }
    });
    this.instances.clear();
  }

  /* =================================================
     🧠 CORE SWIPER INIT (AS-IS)
     ================================================= */
  initSwiper(el) {
    if (el.classList.contains("swiper-initialized")) return;
    if (typeof Swiper === "undefined") return;

    const type = el.dataset.swiperType;
    const config = this.getConfig(el, type);

    const swiper = new Swiper(el, config);
    this.instances.set(el, swiper);

    /* ✅ CATEGORY SECTION: padding-left 0 (AS-IS) */
    if (type === "category" || type === "category-section") {
      swiper.el.style.setProperty("padding-left", "0px", "important");
      if (swiper.wrapperEl) {
        swiper.wrapperEl.style.setProperty("padding-left", "0px", "important");
      }
    }

    /* 🧱 HEIGHT FIX AFTER IMAGE LOAD (AS-IS) */
    el.querySelectorAll("img").forEach((img) => {
      img.addEventListener("load", () => {
        if (swiper.params.autoHeight) {
          swiper.updateAutoHeight(100);
        }
      });
    });
  }

  /* =================================================
     ⚙️ CONFIG BUILDER (AS-IS – NO LOGIC REMOVED)
     ================================================= */
  getConfig(el, type) {
    const loopEnabled = el.dataset.swiperLoop === "true";
    const perRow = {
      mobile: parseFloat(el.dataset.perRowMobile) || 1.2,
      ipadVertical: parseFloat(el.dataset.perRowIpadVertical) || 2,
      ipadHorizontal: parseFloat(el.dataset.perRowIpadHorizontal) || 3,
      mac: parseFloat(el.dataset.perRowMac) || 4,
      desktop: parseFloat(el.dataset.perRowDesktop) || 5,
    };

    const gap = {
      mobile: parseInt(el.dataset.gapMobile) || 16,
      ipadVertical: parseInt(el.dataset.gapIpadVertical) || 20,
      ipadHorizontal: parseInt(el.dataset.gapIpadHorizontal) || 24,
      mac: parseInt(el.dataset.gapMac) || 28,
      desktop: parseInt(el.dataset.gapDesktop) || 32,
    };

    const beforeOffset = {
      mobile: parseInt(el.dataset.mobileBeforeOffset) || 0,
      ipadVertical: parseInt(el.dataset.ipadVerticalBeforeOffset) || 0,
      ipadHorizontal: parseInt(el.dataset.ipadHorizontalBeforeOffset) || 0,
      mac: parseInt(el.dataset.macBeforeOffset) || 0,
      desktop: parseInt(el.dataset.desktopBeforeOffset) || 0,
    };

    const afterOffset = {
      mobile: parseInt(el.dataset.mobileAfterOffset) || 0,
      ipadVertical: parseInt(el.dataset.ipadVerticalAfterOffset) || 0,
      ipadHorizontal: parseInt(el.dataset.ipadHorizontalAfterOffset) || 0,
      mac: parseInt(el.dataset.macAfterOffset) || 0,
      desktop: parseInt(el.dataset.desktopAfterOffset) || 0,
    };

    const directionMobile =
      el.dataset.mobileSwiperDirection ||
      el.dataset.directionMobile ||
      "horizontal";

    const baseConfig = {
      direction: directionMobile === "vertical" ? "vertical" : "horizontal",
      loop: loopEnabled,
      slidesPerView: perRow.mobile,
      spaceBetween: gap.mobile,
      slidesOffsetBefore: beforeOffset.mobile,
      slidesOffsetAfter: afterOffset.mobile,

      watchOverflow: true,

      // 🔥 ANDROID PERFORMANCE FIX
      observer: !IS_ANDROID,
      observeParents: !IS_ANDROID,
      resizeObserver: !IS_ANDROID,

      preloadImages: false,
      lazy: IS_ANDROID ? false : { loadOnTransitionStart: true },

      autoHeight: directionMobile === "vertical",

      breakpoints: {
        600: {
          direction: "horizontal",
          slidesPerView: perRow.ipadVertical,
          spaceBetween: gap.ipadVertical,
          slidesOffsetBefore: beforeOffset.ipadVertical,
          slidesOffsetAfter: afterOffset.ipadVertical,
        },
        834: {
          direction: "horizontal",
          slidesPerView: perRow.ipadHorizontal,
          spaceBetween: gap.ipadHorizontal,
          slidesOffsetBefore: beforeOffset.ipadHorizontal,
          slidesOffsetAfter: afterOffset.ipadHorizontal,
        },
        1024: {
          direction: "horizontal",
          slidesPerView: perRow.mac,
          spaceBetween: gap.mac,
          slidesOffsetBefore: beforeOffset.mac,
          slidesOffsetAfter: afterOffset.mac,
        },
        1440: {
          direction: "horizontal",
          slidesPerView: perRow.desktop,
          spaceBetween: gap.desktop,
          slidesOffsetBefore: beforeOffset.desktop,
          slidesOffsetAfter: afterOffset.desktop,
        },
      },

      on: {
        init: (swiper) => {
          el.classList.add("swiper-initialized");
        },
      },
    };


    switch (type) {
      case "video":
        return this.extendVideoSwiper(baseConfig, el);
      case "shop-the-look":
        return this.extendShopTheLookSwiper(baseConfig, el);
      case "banner":
        return this.extendBannerSwiper(baseConfig, el);
      case "featured-collection":
        return this.extendFeaturedSwiper(baseConfig, el);
      case "category":
        return this.extendCategorySwiper(baseConfig, el);
      case "wear-with-it":
        return this.extendWearWithItSwiper(baseConfig, el);
      case "shop-the-look-2-0":
        return this.extendShopTheLookSwiper2(baseConfig, el);
      default:
        return baseConfig;
    }
  }

  /* =================================================
     🧩 EXTENSIONS (100% AS-IS)
     ================================================= */

  extendVideoSwiper(config, el) {
    const totalSlides = el.querySelectorAll(".swiper-slide").length;
    const isMobile = window.innerWidth < 600;

    return {
      ...config,
      loop: !isMobile && totalSlides > 1,
      centeredSlides: !isMobile,
      on: {
        init: () => this.lazyLoadVideos(el),
        slideChangeTransitionEnd: () => {
          el.querySelectorAll("video").forEach((v) => v.pause());
          el
            .querySelectorAll(".swiper-slide-active video")
            .forEach((v) => v.play().catch(() => { }));
        },
      },
    };
  }

  lazyLoadVideos(section) {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const video = entry.target;
          const source = video.querySelector("source");
          if (entry.isIntersecting && source?.dataset.src && !source.src) {
            source.src = source.dataset.src;
            video.load();
          }
        });
      },
      { threshold: 0.4 }
    );
    section.querySelectorAll("video").forEach((v) => observer.observe(v));
  }

  extendShopTheLookSwiper(config, el) {
    return {
      ...config,
      pagination: {
        el: el.querySelector(".swiper-pagination"),
        clickable: true,
        dynamicBullets: true,
      },
    };
  }

  extendBannerSwiper(config, el) {
    const directionMobile =
      el.dataset.mobileSwiperDirection ||
      el.dataset.directionMobile ||
      "horizontal";

    const isVertical =
      window.innerWidth < 768 && directionMobile === "vertical";

    return {
      ...config,
      direction: isVertical ? "vertical" : "horizontal",
      // 🧱 FIX: vertical mode height correction
      autoHeight: isVertical,
      navigation: {
        nextEl: el.querySelector(".swiper-button-next"),
        prevEl: el.querySelector(".swiper-button-prev"),
      },
      scrollbar: isVertical
        ? { el: el.querySelector(".swiper-scrollbar"), draggable: true, hide: false }
        : false,
      on: {
        init: (swiper) => {
          el.classList.add("swiper-initialized");
          this.syncBannerHeight(el, swiper);
          // 🧱 FIX: update height after init
          if (swiper.params.autoHeight) swiper.updateAutoHeight(100);
        },
        slideChangeTransitionEnd: (swiper) => {
          // 🧱 FIX: recalc height on each slide
          if (swiper.params.autoHeight) swiper.updateAutoHeight(100);
        },
      },
    };
  }

  syncBannerHeight(el, swiper) {
    const bannerImg = el.closest("[data-section-id]")?.querySelector(".banner-image");
    if (!bannerImg) return;
    const adjustHeight = () => {
      if (swiper.params.direction !== "vertical") return;
      const h = bannerImg.clientHeight || bannerImg.offsetHeight;
      swiper.el.style.height = `${h}px`;
    };
    adjustHeight();
    window.addEventListener("resize", adjustHeight);
  }

  extendFeaturedSwiper(config, el) {
    return {
      ...config,
      navigation: {
        nextEl: el.querySelector(".swiper-button-next"),
        prevEl: el.querySelector(".swiper-button-prev"),
      },
    };
  }

  extendCategorySwiper(config, el) {
    return {
      ...config,
      slidesPerView: "auto",
      grabCursor: true,
      watchSlidesProgress: true,
      // 🧱 FIX: safe for vertical category swipers
      autoHeight: config.direction === "vertical",
      on: {
        init: () => el.classList.add("swiper-initialized"),
      },
    };
  }

  extendWearWithItSwiper(config, el) {
    const desk_item = parseFloat(el.dataset.desktop_items) || 3;
    const mob_item = parseFloat(el.dataset.mobile_items) || 1.5;
    const ipad_vert = parseFloat(el.dataset.ipad_vertical_items) || 2.5;
    const ipad_hori = parseFloat(el.dataset.ipad_horizontal_items) || 2.5;
    const mob_padding = parseFloat(el.dataset.mobile_padding) || 10;
    const ipad_vert_padding = parseFloat(el.dataset.ipad_vertical_padding) || 10;
    const ipad_hori_padding = parseFloat(el.dataset.ipad_horizontal_padding) || 10;
    const desk_padding = parseFloat(el.dataset.desktop_padding) || 10;

    return {
      ...config,
      slidesPerView: mob_item,
      spaceBetween: mob_padding,
      loop: false,
      watchSlidesProgress: true,
      navigation: {
        nextEl: ".swiper-button-next.wear-with-it-arrow",
        prevEl: ".swiper-button-prev.wear-with-it-arrow",
      },
      breakpoints: {
        0: { slidesPerView: mob_item, spaceBetween: mob_padding },
        480: { slidesPerView: ipad_vert, spaceBetween: ipad_vert_padding },
        768: { slidesPerView: ipad_hori, spaceBetween: ipad_hori_padding },
        1100: { slidesPerView: desk_item, spaceBetween: desk_padding },
      },
      on: {
        init() {
          el.classList.add("loaded");
        },
      },
    };
  }
  extendShopTheLookSwiper2(config, el) {
    return {
      ...config,

      navigation: {
        nextEl: el.querySelector(".swiper-button-next"),
        prevEl: el.querySelector(".swiper-button-prev"),
      },

      pagination: {
        el: el.querySelector(".swiper-pagination"),
        clickable: true,
        dynamicBullets: true,
      },

      on: {
        init: (swiper) => {
          const section = el.closest('[data-section-type="shop-the-look-2-0"]');
          if (!section) return;

          section.querySelectorAll(".stl-dot").forEach((dot) => {
            dot.addEventListener("click", () => {
              const index = Number(dot.dataset.targetSlide);
              if (!Number.isNaN(index)) {
                swiper.slideTo(index, 300);
              }
            });
          });
        },
      },
    };
  }



}

/* =================================================
   🚀 BOOTSTRAP (AS-IS)
   ================================================= */
document.addEventListener("DOMContentLoaded", () => {
  window.globalSwiperManager = new GlobalSwiperManager();
});
